import { useAppFlags } from './useAppFlags';

/**
 * The wildfire feature mode for the current customer, sourced from the
 * LaunchDarkly `flag-wildfire` string variation.
 *
 * - `legacy`     — AEP-style: legacy tabular WPL only. No fuel data, no group summary.
 * - `fuels-only` — Wildfire objective and WPL filter shown; WPL uses stored legacy
 *                  values. Fuels block in span popover;
 *                  fuel hazard map layer/legend visible. No burn probability,
 *                  no group summary.
 * - `all`        — Full Wildfire Intelligence: new group risk summary UI, fuels in
 *                  span, fuel hazard layer, burn probability + drivers. WPL is
 *                  always sourced from the dynamic burnability CTE.
 * - `none`       — No wildfire UI at all.
 */
export type WildfireMode = 'legacy' | 'fuels-only' | 'all' | 'none';

const VALID: readonly WildfireMode[] = ['legacy', 'fuels-only', 'all', 'none'];

/**
 * Read flag-wildfire and return the typed mode. Defaults to `'none'` when the flag
 * is absent or has an unexpected value, so an unconfigured user sees no wildfire UI.
 */
export const useWildfireMode = (): WildfireMode => {
  const raw = useAppFlags().flagWildfireMode;
  return VALID.includes(raw as WildfireMode) ? (raw as WildfireMode) : 'none';
};

// Wildfire objective is offered for any mode but 'none'.
export const useHasWildfireObjective = (): boolean =>
  useWildfireMode() !== 'none';

// FDMA-driven content: span fuels block + map fuel-hazard layer/legend +
// /v1/statistics/fuel_hazard endpoint.
export const useHasFuelsData = (): boolean => {
  const m = useWildfireMode();
  return m === 'fuels-only' || m === 'all';
};

// Group risk summary UI (Summary tab) + burn probability driver charts.
export const useHasGroupRiskSummary = (): boolean =>
  useWildfireMode() === 'all';

// Identifies the AEP-style mode without fuel data.
export const useHasLegacyWpl = (): boolean => useWildfireMode() === 'legacy';
